import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './RateCard.css';

const TOC = [
  { id: 'overview',      label: 'Overview' },
  { id: 'fixed',         label: 'Fixed Price',           indent: true },
  { id: 'rate-per-unit', label: 'Rate Per Unit',         indent: true },
  { id: 'tiered',        label: 'Graduated & Volume',    indent: true },
  { id: 'minmax',        label: 'Min / Max',             indent: true },
];

function RcTable({ headers, rows }) {
  return (
    <div className="rc-table-wrap">
      <table className="rc-table">
        <thead>
          <tr>{headers.map((h) => <th key={h} className="rc-th">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j} className="rc-td">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PricingModels({ onNavigate }) {
  const { industry } = useIndustry();
  const rc = industry.rateCard;
  const { currency, feeComponent, unit, tiers } = rc;

  const fmt = (n) => `${currency}${n}`;
  const tierRows = tiers.map((t) => [t.start, t.limit === null ? '∞' : t.limit, fmt(t.rate), t.flat ? fmt(t.flat) : '—']);

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Billing · Rate Card"
        title="Pricing Models"
        description="A Pricing Model is the rule a rate card uses to convert a usage quantity into a charge — whether it scales linearly, in tiers, as a flat amount, or by another formula."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'rate-card', label: 'Rate Cards' },
        ]}
      />

      <Section id="overview" title="Overview">
        <RcTable
          headers={['Model', 'How it works']}
          rows={[
            ['Fixed Price',       'Flat amount charged regardless of usage'],
            ['Rate Per Unit',     'Every unit billed at the same rate'],
            ['Graduated Pricing', 'Usage split across tiers; each slice billed at its tier\'s rate'],
            ['Volume Based',      'Entire usage billed at the rate of the single tier it falls into'],
            ['Min / Max Pricing', 'Higher or lower of a flat fee and a usage-based fee'],
          ]}
        />

        <RcTable
          headers={['If you want to…', 'Use this model']}
          rows={[
            ['Charge a flat subscription fee regardless of usage',                'Fixed Price'],
            ['Bill proportionally — more usage, higher charge',                   'Rate Per Unit'],
            ['Reward volume with lower per-unit rates at each tier',              'Graduated'],
            ['Apply a single discounted rate once a volume threshold is crossed', 'Volume Based'],
            ['Guarantee a revenue floor at low usage',                            'Min / Max — Max mode'],
            ['Cap customer charges regardless of high usage',                     'Min / Max — Min mode'],
            ['Include a bundle of free units before charging for overages',       'Graduated (₹0 first tier + flat fee) or Grant'],
          ]}
        />

        <Callout type="tip">
          Use the <strong>Calculator</strong> to enter a usage value and see the step-by-step
          charge for any model with custom parameters.
        </Callout>
      </Section>

      <Section id="models" title="Models">
        <SubSection id="fixed" title="Fixed Price">
          <Para>
            A flat amount is charged every billing period regardless of usage — the same charge
            whether the customer recorded zero units or thousands. Use this for access or capacity
            charges: platform fees, monthly subscriptions, reserved seats.
          </Para>
          <RcTable
            headers={[`${feeComponent} usage (${unit})`, `Price (${currency})`]}
            rows={[
              ['0',  fmt(rc.fixed.price)],
              ['42', fmt(rc.fixed.price)],
              ['89', fmt(rc.fixed.price)],
            ]}
          />
        </SubSection>

        <SubSection id="rate-per-unit" title="Rate Per Unit">
          <Para>
            Every unit is priced at the same rate. The charge is{' '}
            <em>total usage × {fmt(rc.ratePerUnit.rate)}</em> — no thresholds, no tiers. Use
            this when the relationship between usage and value is linear and you want a simple,
            predictable structure.
          </Para>
          <RcTable
            headers={[`Usage (${unit})`, `Price (${currency})`, 'Calculation']}
            rows={rc.ratePerUnit.examples.map((e) => [
              e.usage, fmt(e.price), `${e.usage} × ${fmt(rc.ratePerUnit.rate)}`,
            ])}
          />
        </SubSection>

        <SubSection id="tiered" title="Graduated & Volume Based">
          <Para>
            Both models use a tier structure — each tier has a start, a limit, a per-unit rate,
            and an optional flat fee. The difference is how usage maps to those tiers.
          </Para>
          <RcTable
            headers={['Tier start', 'Tier limit', `Rate (${currency} per unit)`, `Flat fee (${currency})`]}
            rows={tierRows}
          />

          <Para><strong>Graduated</strong> — usage is split across tiers in sequence. The first
            tier's rate applies to the first slice, the second tier's rate to the next, and so on.
            Each slice is only discounted for the usage that actually falls within it. Use this to
            reward volume progressively without retroactively repricing earlier usage.
          </Para>
          <RcTable
            headers={[`Usage (${unit})`, `Price (${currency})`, 'Calculation']}
            rows={rc.graduated.examples.map((e) => [e.usage, fmt(e.price), e.breakdown])}
          />

          <Para><strong>Volume Based</strong> — the entire usage is priced at the rate of the
            single tier it falls into. Crossing a threshold lowers the per-unit price retroactively
            on all units. Use this when the intent is one bulk rate for all usage once a threshold
            is crossed.
          </Para>
          <RcTable
            headers={[`Usage (${unit})`, `Price (${currency})`, 'Calculation']}
            rows={rc.volume.examples.map((e) => [e.usage, fmt(e.price), e.breakdown])}
          />

          <Callout type="info" title="Graduated vs Volume — same tiers, different math">
            Graduated splits usage across tiers — each slice at its tier's rate. Volume matches
            the total to one tier and prices all units at that single rate. Near a tier boundary,
            a Volume customer can pay <em>less</em> with slightly more usage because the lower
            tier rate applies to everything.
          </Callout>
          <Callout type="info" title="Tier flat fees">
            Each tier can carry a flat fee in addition to the per-unit rate. For Graduated, the
            flat fee applies to every tier that has usage. For Volume, only the matched tier's
            flat fee applies. If proration is enabled on the rate card, tier flat fees are
            prorated by days active.
          </Callout>
          <Callout type="info" title="Repeat tiers (Graduated only)">
            The last tier can be marked as a <strong>repeating bracket</strong>. Instead of
            extending to infinity at one rate, it repeats the same bracket width, rate, and flat
            fee for every subsequent slice. Define 0–100 and 100–150, mark 100–150 as repeat,
            and the system applies the same bracket to 150–200, 200–250, and so on.
          </Callout>
        </SubSection>

        <SubSection id="minmax" title="Min / Max Pricing">
          <Para>
            The charge is the higher (<strong>Max mode</strong>) or lower (<strong>Min mode</strong>)
            of a flat fee and a usage-based fee. Use Max to guarantee a revenue floor at low usage;
            use Min to cap charges regardless of spikes.
          </Para>
          <RcTable
            headers={['Variant', 'Use case']}
            rows={[
              ['Maximum of (flat fee, usage-based)', 'Minimum commitment — guarantees a revenue floor at low usage'],
              ['Minimum of (flat fee, usage-based)', 'Discount cap — limits the charge regardless of high usage'],
            ]}
          />
          <RcTable
            headers={[
              `Usage (${unit})`, `Flat fee (${currency})`,
              `Usage-based (${currency})`, 'Logic', `Final price (${currency})`,
            ]}
            rows={rc.minMax.examples.map((e) => [
              e.usage, e.flatFee,
              `${e.calcNote} = ${e.calculated}`,
              `${e.logic} of (${e.flatFee}, ${e.calculated})`,
              <strong key="p">{e.price}</strong>,
            ])}
          />
        </SubSection>
      </Section>
    </DocPage>
  );
}
